/**
 * Predictive Search — fullscreen modal with:
 * - Empty state: recent searches (localStorage) + featured collections
 * - Loading skeleton while fetching
 * - Results: suggestions | collections | product grid (2 cols)
 * - Keyboard navigation, Cmd+K shortcut, debounced input
 */
import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { DialogCloseEvent, DialogOpenEvent } from '@theme/dialog';

const RECENTS_KEY = 'theme_search_recents';
const RECENTS_MAX = 5;
const RECENT_PRODUCTS_KEY = 'theme_search_recent_products';
const RECENT_PRODUCTS_MAX = 4;

class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton', 'emptyState'];

  #controller = new AbortController();
  #currentIndex = -1;

  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#controller;

    document.addEventListener('keydown', this.#handleShortcut, { signal });

    if (this.dialog) {
      this.dialog.addEventListener(DialogCloseEvent.eventName, this.#onClose, { signal });
      this.dialog.addEventListener(DialogOpenEvent.eventName, this.#onOpen, { signal });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  #handleShortcut = (event) => {
    if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
      event.preventDefault();
      this.dialog?.toggleDialog();
    }
  };

  #onOpen = () => {
    this.#showEmptyState();
    requestAnimationFrame(() => this.refs.searchInput?.focus());
  };

  #onClose = () => {
    this.#reset();
  };

  /** Debounced search on input */
  search = debounce((event) => {
    if (!event.inputType) return;

    const term = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!term.length) {
      this.#showEmptyState();
      this.#hideResetButton();
      return;
    }

    this.#showResetButton();
    this.#hideEmptyState();
    this.#showResults();
    this.#showLoadingSkeleton();
    this.#getSearchResults(term);
  }, 200);

  /** Keyboard navigation */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#reset();
      this.dialog?.closeDialog();
      return;
    }

    const items = this.#getAllItems();
    if (!items.length) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.#currentIndex = this.#currentIndex < items.length - 1 ? this.#currentIndex + 1 : 0;
        this.#highlightItem(items);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.#currentIndex = this.#currentIndex > 0 ? this.#currentIndex - 1 : items.length - 1;
        this.#highlightItem(items);
        break;
      case 'Enter':
        if (this.#currentIndex >= 0 && items[this.#currentIndex]) {
          event.preventDefault();
          items[this.#currentIndex].querySelector('a')?.click();
        }
        break;
    }
  };

  onSubmit() {
    const term = this.refs.searchInput?.value.trim();
    if (term) this.#saveRecentSearch(term);
  }

  resetSearch = debounce(() => {
    this.refs.searchInput.focus();
    this.#reset();
    this.#showEmptyState();
  }, 100);

  clearRecents() {
    localStorage.removeItem(RECENTS_KEY);
    this.#renderRecents();
  }

  // ─── Empty state ─────────────────────────────────────────────────────────────

  #showEmptyState() {
    this.#renderRecents();
    this.#renderRecentProducts();
    if (this.refs.emptyState) this.refs.emptyState.hidden = false;
    this.#hideResults();
  }

  #hideEmptyState() {
    if (this.refs.emptyState) this.refs.emptyState.hidden = true;
  }

  #showResults() {
    if (this.refs.predictiveSearchResults) this.refs.predictiveSearchResults.hidden = false;
  }

  #hideResults() {
    if (this.refs.predictiveSearchResults) {
      this.refs.predictiveSearchResults.hidden = true;
      this.refs.predictiveSearchResults.innerHTML = '';
    }
  }

  // ─── Recent searches ─────────────────────────────────────────────────────────

  #getRecentSearches() {
    try { return JSON.parse(localStorage.getItem(RECENTS_KEY) || '[]'); }
    catch { return []; }
  }

  #saveRecentSearch(term) {
    const recents = this.#getRecentSearches().filter(r => r !== term);
    recents.unshift(term);
    localStorage.setItem(RECENTS_KEY, JSON.stringify(recents.slice(0, RECENTS_MAX)));
  }

  #renderRecents() {
    const recentsSection = this.refs.recentsSection;
    const recentsList = this.refs.recentsList;
    if (!recentsSection || !recentsList) return;

    const recents = this.#getRecentSearches();
    if (!recents.length) {
      recentsSection.hidden = true;
      return;
    }

    recentsSection.hidden = false;
    recentsList.innerHTML = recents.map(term => `
      <li>
        <a
          href="${Theme.routes.search_url}?q=${encodeURIComponent(term)}&options[prefix]=last"
          class="tw:inline-flex tw:items-center tw:gap-2 tw:px-4 tw:py-2 tw:rounded-full tw:border tw:border-border tw:text-sm tw:text-foreground tw:no-underline tw:hover:border-foreground tw:hover:bg-surface tw:transition-colors"
        >
          <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true" class="tw:shrink-0 tw:text-muted">
            <polyline points="1 4 1 10 7 10"></polyline>
            <path d="M3.51 15a9 9 0 1 0 .49-4.93"></path>
          </svg>
          <span>${term}</span>
        </a>
      </li>
    `).join('');

    // Save to recents on click
    recentsList.querySelectorAll('a').forEach((a, i) => {
      a.addEventListener('click', () => this.#saveRecentSearch(recents[i]), { once: true });
    });
  }

  // ─── Recent products ─────────────────────────────────────────────────────────

  #getRecentProducts() {
    try { return JSON.parse(localStorage.getItem(RECENT_PRODUCTS_KEY) || '[]'); }
    catch { return []; }
  }

  #saveRecentProduct(productData) {
    const recents = this.#getRecentProducts().filter(p => p.url !== productData.url);
    recents.unshift(productData);
    localStorage.setItem(RECENT_PRODUCTS_KEY, JSON.stringify(recents.slice(0, RECENT_PRODUCTS_MAX)));
  }

  #renderRecentProducts() {
    const section = this.refs.recentProductsSection;
    const grid = this.refs.recentProductsGrid;
    if (!section || !grid) return;

    const products = this.#getRecentProducts();
    if (!products.length) {
      section.hidden = true;
      return;
    }

    section.hidden = false;
    grid.innerHTML = products.map(p => `
      <a href="${p.url}" class="tw:group tw:no-underline tw:text-foreground tw:flex tw:flex-col tw:gap-2">
        <div class="tw:relative tw:aspect-[3/4] tw:overflow-hidden tw:rounded-[var(--style-border-radius-cards)] tw:bg-surface">
          ${p.image ? `<img src="${p.image}" alt="${p.title}" loading="lazy" class="tw:w-full tw:h-full tw:object-cover tw:transition-transform tw:duration-500 tw:[transition-timing-function:var(--ease-out-expo)] tw:group-hover:scale-[1.04]">` : ''}
        </div>
        <div class="tw:flex tw:flex-col tw:gap-0.5">
          <span class="tw:text-sm tw:font-medium tw:truncate">${p.title}</span>
          <span class="tw:text-sm tw:font-semibold">${p.price}</span>
        </div>
      </a>
    `).join('');

    grid.querySelectorAll('a').forEach(a => {
      a.addEventListener('click', () => this.dialog?.closeDialog(), { once: true });
    });
  }

  #extractProductDataFromLink(link) {
    const card = link.closest('li') || link;
    const img = card.querySelector('img');
    const title = card.querySelector('.tw\\:truncate')?.textContent?.trim() || '';
    const price = card.querySelector('.tw\\:font-semibold')?.textContent?.trim() || '';
    return {
      url: link.getAttribute('href'),
      title,
      price,
      image: img?.getAttribute('src') || ''
    };
  }

  // ─── Loading skeleton ─────────────────────────────────────────────────────────

  #showLoadingSkeleton() {
    if (!this.refs.predictiveSearchResults) return;

    const skeletonCard = `
      <div class="search-skeleton-card">
        <div class="search-skeleton-image"></div>
        <div class="search-skeleton-line"></div>
        <div class="search-skeleton-line search-skeleton-line--short"></div>
      </div>
    `;

    this.refs.predictiveSearchResults.innerHTML = `
      <div class="search-skeleton-suggestions">
        <div class="search-skeleton-line tw:mb-2"></div>
        <div class="search-skeleton-line"></div>
        <div class="search-skeleton-line search-skeleton-line--short"></div>
      </div>
      <div class="search-skeleton-grid">
        ${[1,2,3,4].map(() => skeletonCard).join('')}
      </div>
    `;
  }

  // ─── Fetch results ────────────────────────────────────────────────────────────

  async #getSearchResults(term) {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('resources[type]', 'product,query,collection');
    url.searchParams.set('resources[limit]', '6');
    url.searchParams.set('resources[limit_scope]', 'each');

    try {
      const html = await sectionRenderer.getSectionHTML(sectionId, false, url);
      if (!html) return;

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newResults = doc.getElementById('predictive-search-results');

      if (newResults && this.refs.predictiveSearchResults) {
        this.refs.predictiveSearchResults.innerHTML = newResults.innerHTML;

        this.refs.predictiveSearchResults.querySelectorAll('a[ref="resultsItems[]"]').forEach(a => {
          a.addEventListener('click', () => {
            this.#saveRecentSearch(term);
            const productData = this.#extractProductDataFromLink(a);
            if (productData.url && productData.title) {
              this.#saveRecentProduct(productData);
            }
            this.dialog?.closeDialog();
          }, { once: true });
        });
      }
    } catch (err) {
      console.error('Predictive search error:', err);
    }
  }

  // ─── Helpers ──────────────────────────────────────────────────────────────────

  #reset() {
    this.refs.searchInput.value = '';
    this.#currentIndex = -1;
    this.#hideResetButton();
    this.#hideResults();
  }

  #showResetButton() {
    if (this.refs.resetButton) this.refs.resetButton.hidden = false;
  }

  #hideResetButton() {
    if (this.refs.resetButton) this.refs.resetButton.hidden = true;
  }

  #getAllItems() {
    if (this.refs.predictiveSearchResults?.hidden) return [];
    return [...this.refs.predictiveSearchResults.querySelectorAll('[ref="resultsItems[]"]')];
  }

  #highlightItem(items) {
    items.forEach((item, i) => {
      if (i === this.#currentIndex) {
        item.setAttribute('aria-selected', 'true');
        item.style.background = 'var(--color-surface)';
        item.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      } else {
        item.removeAttribute('aria-selected');
        item.style.background = '';
      }
    });
    this.refs.searchInput.focus();
  }
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
